import React, { type FC, useEffect, useMemo, useState } from 'react'
import { MetersPagination } from '../../common/pagination/MetersPagination'
import LoraLogo from '../../../assets/lora.png'
import { CustomTableHead } from './CustomTableHead'
import { StyledTable, StyledTableBody } from './MetersTable.style'
import { MetersTableRow } from './meters-table-row/MetersTableRow'
import { dateToLocaleOrEmpty, paginateList, parseCubicMeters } from '../../../utils/utils'
import { MeterConfiguration } from '../meter-configuration/MeterConfiguration'
import { FetchMetersAction, SetMeterInEditorAction } from '../../../redux/meters/meter-actions'
import { type Sort, sortFilteredMeters, SortOrder, SortType } from '../../../utils/sortUtils'
import moment from 'moment'
import {
  SetMeterActivePageAction,
  SetMeterPageSizeAction,
  ToggleMeterSettingsDialogAction
} from '../../../redux/meter-table/meter-table-actions'
import { isPulseDevice, type Meter } from '../../../redux/meters/meter-types'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import { type RootState } from '../../../redux/store'

const INITIAL_SORTING: Sort = { type: SortType.ByName, order: SortOrder.Ascending }
const REFRESH_INTERVAL_30_MINUTES = 1800_000 // 30 minutes (in millis)

/**
 * Meters table UI component.
 */
export const MetersTable: FC = () => {
  const dispatch = useAppDispatch()
  const filteredMeters = useAppSelector((state: RootState) => state.data.filteredMeters)
  const meterActivePage = useAppSelector((state: RootState) => state.meterTable.meterActivePage)
  const meterPageSize = useAppSelector((state: RootState) => state.meterTable.meterPageSize)

  const filter = { filteredMeters }

  const [idVisible, setIdVisible] = useState(true)
  const [usageVisible, setUsageVisible] = useState(true)
  const [labelVisible, setLabelVisible] = useState(true)
  const [activeSort, setActiveSort] = useState<Sort>(INITIAL_SORTING)

  const sortedList = useMemo(() =>
    sortFilteredMeters(filter.filteredMeters, activeSort), [filter.filteredMeters, activeSort])

  const filteredAndPaginatedMeters = useMemo(() =>
    paginateList(sortedList, meterActivePage, meterPageSize), [sortedList, meterActivePage, meterPageSize])

  useEffect(() => {
    const interval = setInterval(() => {
      // Refresh meters data (incl. readings visible in the table)
      dispatch(FetchMetersAction())
    }, REFRESH_INTERVAL_30_MINUTES)

    return () => {
      clearInterval(interval)
    }
  }, [dispatch])

  useEffect(() => {
    // Initial fetching of meter data
    dispatch(FetchMetersAction())
  }, [dispatch])

  const onToggleUsageVisibility = (): void => {
    setUsageVisible(!usageVisible)
  }

  const onToggleIdVisibility = (): void => {
    setIdVisible(!idVisible)
  }

  const onToggleLabelVisibility = (): void => {
    setLabelVisible(!labelVisible)
  }
  const handlePageChange = (pageNumber: number): void => {
    dispatch(SetMeterActivePageAction({ activePage: pageNumber }))
  }

  const changePageSize = (newPageSize: number): void => {
    dispatch(SetMeterPageSizeAction({ pageSize: newPageSize }))
  }

  return (
        <div>
            <MeterConfiguration/>
            <StyledTable>
                <CustomTableHead
                    idVisible={idVisible}
                    labelVisible={labelVisible}
                    usageVisible={usageVisible}
                    onToggleUsageVisibility={onToggleUsageVisibility}
                    onToggleIdVisibility={onToggleIdVisibility}
                    onToggleLabelVisibility={onToggleLabelVisibility}
                    onSortingChange={(sorting: Sort) => {
                      setActiveSort(sorting)
                    }}
                />
                <StyledTableBody>
                    {[...filteredAndPaginatedMeters].map(meter => {
                      return (<MetersTableRowContainer
                            key={meter.id}
                            meter={meter}
                            labelVisible={labelVisible}
                            idVisible={idVisible}
                            usageVisible={usageVisible}

                        />)
                    })}
                </StyledTableBody>
            </StyledTable>
            <MetersPagination
                activePage={meterActivePage}
                itemsCountPerPage={meterPageSize}
                totalItemsCount={filter.filteredMeters.length}
                onPageChange={(page: number) => {
                  handlePageChange(page)
                }}
                onPageSizeChange={(pageSize) => {
                  changePageSize(pageSize)
                }}
            />
        </div>

  )
}

/* Child-components */

interface MetersTableRowContainerProps {
  meter: Meter
  labelVisible: boolean
  idVisible: boolean
  usageVisible: boolean
}

function MetersTableRowContainer(props: Readonly<MetersTableRowContainerProps>): React.ReactElement {
  const { meter, labelVisible, idVisible, usageVisible } = props
  const dispatch = useAppDispatch()

  const waterValue = parseCubicMeters(meter.waterUsed)
  const isPulseMeter = isPulseDevice(meter.type)
  const dateAndTimeUpdateValue = (meter.updatedAt != null) ? moment.unix(meter.updatedAt).format('DD.MM.YYYY HH:mm') : null

  const toggleMeterSettingsDialog = (): void => {
    dispatch(SetMeterInEditorAction(meter))
    dispatch(ToggleMeterSettingsDialogAction())
  }

  return (
        <MetersTableRow
            meter={meter}
            labelVisibility={labelVisible ? undefined : { visibility: 'hidden' }}
            logo={LoraLogo}
            isPulse={isPulseMeter}
            idVisibility={idVisible ? undefined : { visibility: 'hidden' }}
            usageVisibility={usageVisible ? undefined : { visibility: 'hidden' }}
            updatedValue={waterValue}
            dateAndTimeCoupling={dateToLocaleOrEmpty(
              (meter.firstMeterDataEntry != null && meter.firstMeterDataEntry !== 0)
                ? new Date(meter.firstMeterDataEntry * 1000)
                : null
            )}
            onLabelExpandClick={toggleMeterSettingsDialog}
            onSettingsIconClick={toggleMeterSettingsDialog}
            dateAndTimeUpdating={dateAndTimeUpdateValue}
            key={meter.devEui}
        />
  )
}

/* Child-components */
